import { Features } from 'harmony-constants';
import React from 'react';
import { OrgQueryKeys, QueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { useStaticQuery } from '../../../../api/queries/use-static-query';
import { HereMapColors } from '../../../../styles/agistics-colors';
import { Medication, StandardRoute, Tractor, Trailer, type AvoidanceZone, type DeviceLocation, type OrganizationLocation, type TripEvent } from '../../../../types';
import { toSiteDisplayName } from '../../../../utils/data-mapping';
import { localTime } from '../../../../utils/date-time-utils';
import { type HereMapRefObject } from '../../../here-maps/here-map';
import { HereMapContext } from '../../../here-maps/here-map-context';
import { HereMapMarker } from '../../../here-maps/here-map-marker';
import { HereMapRectangle } from '../../../here-maps/here-map-rectangle';
import { HereMapRoute } from '../../../here-maps/here-map-route';
import { StandardRouteLines } from '../../../here-maps/standard-route-lines';
import { SublocationCircle } from '../../../here-maps/sublocation-circle';
import { useHereMapBaseContent } from '../../../here-maps/useHereMapBaseContent';
import { getStopIcon } from '../../../live-map/markers/live-map-stop-icon';
import { useFeatures } from '../../../user/selectors/use-permissions';
import { useDraftLoadContext } from '../../hooks/draft-load-context';
import { translatedTypeString } from '../delivery-log/delivery-log-event-row';
import { MapPlaceholder } from './map-placeholder';

export const HereMapOrderContent = ({ mapRef, deviceLocations, trailerDeviceLocations, tripEvents }: {
    mapRef: React.RefObject<HereMapRefObject>;
    deviceLocations?: DeviceLocation[];
    trailerDeviceLocations?: DeviceLocation[];
    tripEvents?: TripEvent[];
}) => {
    const hasStandardRoutes = useFeatures(Features.StandardRoutes);
    const { data: organizationLocations, organizationId } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: avoidanceZones = [], isLoading: isLoadingAvoidanceZones } = useStaticQuery<AvoidanceZone[]>(QueryKeys.avoidanceZones);
    const { data: medications = [] } = useOrganizationQuery<Medication[]>(OrgQueryKeys.medications);
    const { data: orgStandardRoutes = [] } = useOrganizationQuery<StandardRoute[]>(OrgQueryKeys.standardRoutes, { enabled: hasStandardRoutes });
    const { data: tractors = [] } = useOrganizationQuery<Tractor[]>(OrgQueryKeys.tractors);
    const { data: trailers = [] } = useOrganizationQuery<Trailer[]>(OrgQueryKeys.trailers);
    const { editedLoad: load } = useDraftLoadContext();
    const { deviceHeadings, mappedDeviceLocations, trailerHeadings, mappedTrailerLocations, filteredAvoidanceZones, avoidanceZoneBoxes} = useHereMapBaseContent({deviceLocations, trailerDeviceLocations, avoidanceZones});
    const [organizationSublocations, setOrganizationSublocations] = React.useState<React.ReactNode>(null);
    
    //Check to see if a carrier user is viewing this trip log/map and scope the avoidance zones down
    //just to the global zones, and the ones belonging to the org the load is owned by
    // TODO
    const filterFn = (organizationId !== load.organizationId) ? ((x: AvoidanceZone) => x.organizationId === load.organizationId || x.organizationId === null) : null;

    const hereContext = React.useContext(HereMapContext);

    const tractorWeight = tractors.find(x => x.id === load.tractorId)?.weight;
    const trailerWeight = trailers.find(x => x.id === load.trailerId)?.weight
    const equipmentWeight = (tractorWeight && trailerWeight) ? tractorWeight + trailerWeight : undefined;

    const stopLocations = load.stops
        .sortBy(stop => stop.sequence)
        .map(stop => {
            const orgLoc = organizationLocations?.find(l => l.id === stop.organizationLocationId);
            const organizationSublocations = orgLoc?.orgSubLocations;
            const customGeofence = orgLoc?.customGeofence;
            const tooltip = orgLoc ? toSiteDisplayName(orgLoc.name, orgLoc.description) : '';

            //we only need to pass type, weight, and actual weight if we have equipment, and if we have
            //any weight restricted avoidance zones (incomplete equipment uses all weight zonesd)
            let weightZoneProperties = {};
            if (equipmentWeight && avoidanceZones.some(x => x.weightThreshold)
            ) {
                weightZoneProperties = {
                    type: stop.type,
                    weight: stop.weight,
                    actualWeight: stop.actualWeight,
                }
            }

            return {
                id: stop.id,
                organizationLocationId: orgLoc?.id || -1,
                ...weightZoneProperties,
                customGeofence,
                sequence: stop.sequence,
                tooltip: tooltip,
                organizationSublocations: organizationSublocations,
            };
        })
        .filter(stop => stop.customGeofence?.latitude && stop.customGeofence?.longitude);

    const uniqueSitesFromStopLocations = [...new Map(stopLocations.map(x => [x.organizationLocationId, x])).values()];

    //https://developer.here.com/documentation/examples/maps-js/clustering/marker-clustering
    React.useEffect(() => {
        if (hereContext.map && tripEvents && organizationLocations) {
            //potential bug?
            //delivery log can be refreshed in the backgroud and if events are removed after fetch happens, they are removed from grid, but 'blue' dots are not removed from map.
            //currently it is not possiable to remove events from DB, but may need to remove the layer, if exists and add back
            const dataPoints = tripEvents.map(function (item) {
                /* eslint-disable-next-line */
                return new H.clustering.DataPoint(item.latitude, item.longitude, undefined, item);
            });
            const getBubbleContent = (eventData: TripEvent) => {
                return `<div class='bubble'><div>${localTime(eventData.timestamp)}</div><div>${translatedTypeString(eventData, organizationLocations, avoidanceZones, medications)}</div></div>`;
            };

            const onMarkerClick = (e: H.util.ChangeEvent) => {
                // Get position of the "clicked" marker
                const position = e.target.getGeometry();
                // Get the data associated with that marker
                const data = e.target.getData();
                if (!data.isCluster()) {
                    const bubbleContent = getBubbleContent(data.getData());
                    // @ts-ignore
                    let bubble = onMarkerClick.bubble;

                    if (!bubble) {
                        bubble = new H.ui.InfoBubble(position, {
                            content: bubbleContent
                        });
                        hereContext.ui.addBubble(bubble);
                        // @ts-ignore
                        onMarkerClick.bubble = bubble;
                    } else {
                        bubble.setPosition(position);
                        bubble.setContent(bubbleContent);
                        bubble.open();
                    }

                    // Move map's center to a clicked marker
                    hereContext.map.setCenter(position, true);
                }
            };

            const clusteredDataProvider = new H.clustering.Provider(dataPoints, {
                clusteringOptions: {
                    // Maximum radius of the neighbourhood
                    eps: 4,
                    // minimum weight of points required to form a cluster
                    minWeight: 3
                },
                //theme: clusterTheme,
            });
            clusteredDataProvider.addEventListener('tap', onMarkerClick);

            const clusteringLayer = new H.map.layer.ObjectLayer(clusteredDataProvider);

            //2nd arg is index layer is inserted. We want this layer on top
            hereContext.map?.addLayer(clusteringLayer, 10);
        }
    }, [hereContext.map, tripEvents, organizationLocations]);

    React.useEffect(() => {
        const circles = uniqueSitesFromStopLocations?.flatMap(x => x.organizationSublocations?.map((organizationSublocation, i) => {
            if (organizationSublocation?.customGeofence) {
                return (
                    <SublocationCircle
                        organizationSubLocation={organizationSublocation}
                        key={i} />
                )
            }
            return null;
        }));

        setOrganizationSublocations(circles);
    }, [load]);

    // edit/create load and delivery log
    if (stopLocations.length >= 2 && !isLoadingAvoidanceZones) {
        const waypoints = stopLocations.map(stopLocation => ({
            ...stopLocation,
            latitude: stopLocation.customGeofence?.latitude,
            longitude: stopLocation.customGeofence?.longitude,
        }));

        const markers = waypoints.map((waypoint, i) => (
            <HereMapMarker key={i}
                icon={getStopIcon(waypoint.sequence)}
                latitude={waypoint.latitude}
                longitude={waypoint.longitude}/>
        ));

        const geofences = uniqueSitesFromStopLocations.map((x, i) => {
            const locationColor = organizationLocations?.find(loc => loc.id === x.organizationLocationId)?.orgLocationType?.locationTypeColor?.hexColor;

            if (x.customGeofence) {
                return (
                    <HereMapRectangle
                        key={i}
                        pointA={{ lat: x.customGeofence.bbox.topLeft.latitude, lng: x.customGeofence.bbox.topLeft.longitude }}
                        pointB={{ lat: x.customGeofence.bbox.bottomRight.latitude, lng: x.customGeofence.bbox.bottomRight.longitude }}
                        strokeColor={locationColor || HereMapColors.LOCATION_DEFAULT_STROKE}
                        type='site'
                        changeable={false}
                    >
                        <div>
                            <span className='map-tooltip-label-name'>{x.tooltip}</span>
                        </div>
                    </HereMapRectangle>
                );
            }
        });

        const standardRoutes = waypoints.reduce((result: StandardRoute[], waypoint, i) => {
            if (!waypoints[i + 1]) return result;

            const nextWaypoint = waypoints[i +1];

            const matchingRoute = orgStandardRoutes.find(route => !route.deletedAt
                && route.originId === waypoint.organizationLocationId
                && route.destinationId === nextWaypoint.organizationLocationId);

            if (matchingRoute) result.push(matchingRoute);

            return result;
        }, []);

        return (
            <React.Fragment>
                {markers}
                {avoidanceZoneBoxes}
                {geofences}
                {organizationSublocations}
                {deviceHeadings}
                {trailerHeadings}
                <HereMapRoute calculateRouteFromWaypoints={true} waypoints={waypoints} equipmentWeight={equipmentWeight}
                    avoidanceZones={filteredAvoidanceZones}
                    onRouteDrawn={() => mapRef.current?.fitMapToAllObjects(true, undefined, false)}
                />
                {mappedDeviceLocations.length > 1 &&
                    <HereMapRoute calculateRouteFromWaypoints={false}
                        waypoints={mappedDeviceLocations}
                    />
                }
                {mappedTrailerLocations.length > 1 &&
                    <HereMapRoute calculateRouteFromWaypoints={false} green={true}
                        waypoints={mappedTrailerLocations}
                    />
                }
                {standardRoutes.map(route => 
                    <StandardRouteLines
                        key={route.id}
                        flexiblePolyline={route.flexiblePolyline}
                        onRouteDrawn={() => mapRef.current?.fitMapToAllObjects(true, undefined, false)}
                    />
                )}
            </React.Fragment>
        );
    } else {
        return <MapPlaceholder/>;
    }
};
